import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Tag as TagIcon, Pencil, Trash2, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import JeaButton from "@/components/jea/JeaButton";
import EmptyState from "@/components/jea/EmptyState";
import JeaCard from "@/components/jea/JeaCard";
import Skeleton from "@/components/jea/Skeleton";
import PillButton from "@/components/jea/PillButton";
import NewTagModal from "@/components/tags/NewTagModal";
import { useTags } from "@/context/TagsContext";
import { useAuth } from "@/context/AuthContext";
import { hexToRgba } from "@/lib/tags";
import { cn } from "@/lib/utils";
import ProfilePanel from "@/pages/settings/ProfilePanel";
import SecurityPanel from "@/pages/settings/SecurityPanel";
import NotificationPrefsPanel from "@/pages/settings/NotificationPrefsPanel";
import TeamPanel from "@/pages/settings/TeamPanel";
import { useRolePermissions } from "@/hooks/useRolePermissions";

const SETTINGS_NAV_TAGS_LEGACY = null; // placeholder for old constant

const DeleteTagConfirm = ({ tag, onClose, onConfirm, busy }) => {
  if (!tag) return null;
  return (
    <div className="fixed inset-0 z-[95] bg-black/60 backdrop-blur-[12px] flex items-center justify-center p-4"
         onClick={busy ? undefined : onClose} data-testid="delete-tag-confirm">
      <div className="w-full max-w-[440px] bg-bg-secondary border border-danger/40 rounded-[12px] p-6"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-danger/15 border border-danger/40 flex items-center justify-center shrink-0">
            <AlertTriangle size={18} className="text-danger" />
          </div>
          <div className="flex-1">
            <h3 className="text-white font-bold text-[18px]">Delete tag "{tag.name}"?</h3>
            <p className="text-text-secondary text-[13px] mt-2">
              It's currently assigned to <span className="jea-mono text-white">{tag.debtor_count}</span> debtor(s).
              This will remove the tag from all of them.
            </p>
          </div>
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="h-10 px-4 rounded-[8px] border border-border-default text-text-secondary hover:text-white hover:bg-bg-tertiary text-[13px] font-semibold disabled:opacity-40"
            data-testid="delete-tag-cancel"
          >
            Cancel
          </button>
          <JeaButton
            size="sm"
            variant="danger"
            onClick={onConfirm}
            loading={busy}
            data-testid="delete-tag-confirm-button"
          >
            Delete
          </JeaButton>
        </div>
      </div>
    </div>
  );
};

const TagsSettingsInner = () => {
  const { tags, loading, deleteTag } = useTags();
  const [newOpen, setNewOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const doDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await deleteTag(deleting.id);
      toast.success(`Deleted tag '${deleting.name}'.`);
      setDeleting(null);
    } catch (e) {
      toast.error("Couldn't delete tag.");
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div data-testid="tags-settings">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-white font-bold text-[20px]">Tags</h2>
          <p className="text-text-secondary text-[13px] mt-1">
            Custom labels to organize and filter your debtors.
          </p>
        </div>
        <JeaButton onClick={() => setNewOpen(true)} className="gap-2" data-testid="settings-new-tag">
          <Plus size={14} /> New Tag
        </JeaButton>
      </div>

      {loading ? (
        <div className="space-y-2" data-testid="tags-loading">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-14" />)}
        </div>
      ) : tags.length === 0 ? (
        <EmptyState
          icon={TagIcon}
          title="No tags yet"
          description="Create your first tag to start organizing judgments."
          testid="tags-empty"
        />
      ) : (
        <JeaCard className="p-0 overflow-hidden" data-testid="tags-list">
          {tags.map((t) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.15 }}
              className="flex items-center gap-4 px-5 py-4 border-b border-border-default last:border-b-0 hover:bg-bg-tertiary transition-colors"
              data-testid={`tag-row-${t.slug}`}
            >
              <span
                className="w-6 h-6 rounded-full shrink-0"
                style={{ background: t.color, boxShadow: `0 0 0 4px ${hexToRgba(t.color, 0.15)}` }}
              />
              <div className="flex-1 min-w-0">
                <div className="text-white font-semibold text-[14px]">{t.name}</div>
                <div className="text-text-secondary text-[12px] mt-0.5">
                  {t.debtor_count} judgment{t.debtor_count === 1 ? "" : "s"} · slug <span className="jea-mono">{t.slug}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setEditing(t)}
                className="w-9 h-9 rounded-[8px] border border-border-default text-text-secondary hover:text-accent-pink hover:border-accent-pink/60 inline-flex items-center justify-center"
                aria-label="Edit tag"
                data-testid={`tag-edit-${t.slug}`}
              >
                <Pencil size={14} />
              </button>
              <button
                type="button"
                onClick={() => setDeleting(t)}
                className="w-9 h-9 rounded-[8px] border border-border-default text-text-secondary hover:text-danger hover:border-danger/60 inline-flex items-center justify-center"
                aria-label="Delete tag"
                data-testid={`tag-delete-${t.slug}`}
              >
                <Trash2 size={14} />
              </button>
            </motion.div>
          ))}
        </JeaCard>
      )}

      <NewTagModal open={newOpen} onClose={() => setNewOpen(false)} />
      <NewTagModal
        open={!!editing}
        existing={editing}
        onClose={() => setEditing(null)}
        onUpdated={() => setEditing(null)}
      />
      {deleting && (
        <DeleteTagConfirm
          tag={deleting}
          busy={deleteBusy}
          onClose={() => setDeleting(null)}
          onConfirm={doDelete}
        />
      )}
    </div>
  );
};

const PlaceholderPanel = ({ title }) => (
  <EmptyState
    icon={TagIcon}
    title={`${title} · coming soon`}
    description="Coming soon."
    testid={`settings-${title.toLowerCase().replace(/\s+/g, "-")}-placeholder`}
  />
);

const PROOnlyPanel = () => (
  <EmptyState
    icon={TagIcon}
    title="Tags are a PRO feature"
    description="Upgrade to PRO to create custom labels, tag your judgments, and filter dashboards by tag."
    testid="tags-pro-gate"
  />
);

const SettingsPage = () => {
  const nav = useNavigate();
  const { user } = useAuth();
  const { canManageTeam } = useRolePermissions();
  const [panel, setPanel] = useState("profile");
  const isPro = user?.account_type === "pro";

  const navItems = useMemo(() => {
    const items = [
      { key: "profile",       label: "Profile" },
      { key: "security",      label: "Security" },
      { key: "notifications", label: "Notification Prefs" },
    ];
    if (isPro) items.push({ key: "tags", label: "Tags" });
    // Team tab: only owner + admin can see it (spec §Phase 7D).
    if (isPro && canManageTeam) items.push({ key: "team", label: "Team" });
    return items;
  }, [isPro, canManageTeam]);

  // If the user's role changes (e.g. they lose admin) while on Team tab,
  // fall back to Profile so we never render a hidden panel.
  React.useEffect(() => {
    if (panel === "team" && !canManageTeam) setPanel("profile");
  }, [panel, canManageTeam]);

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <div className="jea-caption">Account</div>
        <h1 className="text-[32px] font-extrabold tracking-[-0.02em] text-white">Settings</h1>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="settings-nav">
        {navItems.map((item) => (
          <PillButton
            key={item.key}
            active={panel === item.key}
            onClick={() => setPanel(item.key)}
            data-testid={`settings-nav-${item.key}`}
          >
            {item.label}
          </PillButton>
        ))}
      </div>

      <div className="pt-2">
        {panel === "profile" && <ProfilePanel />}
        {panel === "security" && <SecurityPanel />}
        {panel === "notifications" && <NotificationPrefsPanel />}
        {panel === "tags" && (isPro ? <TagsSettingsInner /> : <PROOnlyPanel />)}
        {panel === "team" && canManageTeam && <TeamPanel />}
      </div>
    </div>
  );
};

export default SettingsPage;
